import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "../../lib/api";
import { crmApi } from "../../lib/crm-api";
import { queryKeys } from "../../lib/queryKeys";
import type { ContactMethod, CrmContact } from "../../lib/crm-types";
import { Button } from "../ui/Button";
import { cn } from "../ui/cn";
import { Field, Input, Picker } from "./fields";
import {
  formatPhone,
  isValidPhone,
  PhoneField,
  splitPhone,
} from "./PhoneField";

/**
 * The people at this company.
 *
 * A panel, not a page, because that is the product's whole shape: contacts are
 * first-class rows in the database -- a call points at a person, a ticket points
 * at a person -- and subordinate in the UI. Adding one is an action performed
 * *on a company*, which is why it happens here and there is no route that
 * creates a contact on its own.
 *
 * The form itself is a dialog rather than an inline block. This panel lives in
 * a 20rem column beside two others: six fields crammed into that width were
 * cramped to fill in, and while they were open they pushed the list of people
 * -- the thing the panel is *for* -- off the bottom of the column.
 */
export function ContactsPanel({
  companyId,
  companyName,
  contacts,
}: {
  companyId: number;
  companyName: string;
  contacts: CrmContact[];
}) {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);

  return (
    <section className="rounded-lg border border-line">
      <header className="flex items-center gap-2 border-b border-line px-3 py-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-faint">
          People
        </h2>
        <span className="text-[11px] text-faint">{contacts.length}</span>
        {/* Always "Add" now that the form is a dialog -- it carries its own
            cancel, and a button that changes label under the cursor is a
            button people click twice. */}
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="ml-auto text-xs text-brand-600 hover:text-brand-700"
        >
          Add
        </button>
      </header>

      {adding ? (
        <ContactForm
          companyId={companyId}
          companyName={companyName}
          onDone={() => setAdding(false)}
          onCancel={() => setAdding(false)}
        />
      ) : null}

      {editing !== null ? (
        <ContactForm
          companyId={companyId}
          companyName={companyName}
          contact={contacts.find((entry) => entry.id === editing)}
          onDone={() => setEditing(null)}
          onCancel={() => setEditing(null)}
        />
      ) : null}

      {contacts.length === 0 ? (
        <p className="px-3 py-4 text-xs text-faint">
          Nobody yet. People also appear here on their own when they email in
          from this company's domain.
        </p>
      ) : (
        <ul className="divide-y divide-line">
          {contacts.map((contact) => (
            <ContactRow
              key={contact.id}
              contact={contact}
              onEdit={() => setEditing(contact.id)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

const METHOD_LABEL: Record<ContactMethod, string> = {
  call: "Prefers a call",
  email: "Prefers email",
  whatsapp: "Prefers WhatsApp",
};

function ContactRow({
  contact,
  onEdit,
}: {
  contact: CrmContact;
  onEdit: () => void;
}) {
  /*
   * The inferred preference is its own request, and only made when the row is
   * on screen. It is four grouped counts over the message history; doing it
   * inline with the company would be one aggregate per person on every page
   * load, for a line of text nobody reads on most of them.
   */
  const method = useQuery({
    queryKey: queryKeys.crmContactMethod(contact.id),
    queryFn: () => crmApi.contactMethod(contact.id),
    staleTime: 5 * 60_000,
  });

  const resolved = method.data;

  return (
    <li className="group px-3 py-2">
      <div className="flex items-baseline gap-2">
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-content">
          {contact.name ?? contact.email}
        </span>
        {contact.isPrimary ? (
          <span className="shrink-0 rounded bg-brand-50 px-1 text-[9px] uppercase text-brand-700 dark:bg-brand-950 dark:text-brand-300">
            primary
          </span>
        ) : null}
        <button
          type="button"
          onClick={onEdit}
          aria-label={`Edit ${contact.name ?? contact.email}`}
          className="hidden shrink-0 text-[11px] text-faint hover:text-brand-600 dark:hover:text-brand-400 group-hover:block"
        >
          Edit
        </button>
      </div>

      {contact.role ? (
        <div className="text-[11px] text-muted">{contact.role}</div>
      ) : null}

      <div className="mt-0.5 flex flex-col gap-0.5 text-[11px]">
        <a
          href={`mailto:${contact.email}`}
          className="truncate text-muted hover:text-brand-600"
        >
          {contact.email}
        </a>
        {contact.phone ? (
          /* Stored E.164 in the href, where a dialler wants it; grouped for
             reading in the text. The old strip-the-spaces was doing this job
             back when the column held whatever somebody typed. */
          <a
            href={`tel:${contact.phone}`}
            className="text-muted hover:text-brand-600"
          >
            {formatPhone(contact.phone)}
          </a>
        ) : null}
      </div>

      {/*
        Shown with its basis, always. "Prefers a call" as a bare fact next to a
        phone number reads as something the customer told us; most of the time
        it is something we noticed, and the difference matters to whoever is
        about to act on it.
      */}
      {resolved?.method ? (
        <div className="mt-1 text-[11px] text-faint">
          {METHOD_LABEL[resolved.method]}
          {resolved.basis === "observed" ? (
            <span
              title={`${resolved.counts.call} calls, ${resolved.counts.email} emails, ${resolved.counts.whatsapp} WhatsApp`}
            >
              {" "}
              — from what they do
            </span>
          ) : (
            " — set by hand"
          )}
        </div>
      ) : null}
    </li>
  );
}

/**
 * Add or edit somebody, as a dialog.
 *
 * The same form for both, because they are the same six fields and two copies
 * would be two places for "preferred contact" to drift. The overlay chrome is
 * `CompanyDialog`'s, deliberately -- these two open from the same page and one
 * of them looking different would read as a bug.
 */
function ContactForm({
  companyId,
  companyName,
  contact,
  onDone,
  onCancel,
}: {
  companyId: number;
  companyName: string;
  contact?: CrmContact;
  onDone: () => void;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState(contact?.email ?? "");
  const [name, setName] = useState(contact?.name ?? "");
  const [role, setRole] = useState(contact?.role ?? "");
  /* Split out of the stored E.164 once, on open. The pair is what the two
     controls edit; the server puts them back together. */
  const [phone, setPhone] = useState(() => splitPhone(contact?.phone));
  const [method, setMethod] = useState(contact?.preferredContactMethod ?? "");
  const [isPrimary, setIsPrimary] = useState(contact?.isPrimary ?? false);

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name: name.trim() || null,
        role: role.trim() || null,
        phone: phone.number.trim() || null,
        /* Sent even when the box is empty, and harmless then: it costs nothing
           and means a number added later in the same session is read as the
           country still showing beside it. */
        phoneCountry: phone.country,
        preferredContactMethod: (method || null) as ContactMethod | null,
        isPrimary,
      };

      return contact
        ? crmApi.updateContact(contact.id, { ...payload, email: email.trim() })
        : crmApi.createContact(companyId, { ...payload, email: email.trim() });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.crmCompany(companyId),
      });
      onDone();
    },
  });

  /*
   * The one refusal that is really a question. The server knows exactly who
   * and where, so rather than a red sentence telling somebody to go and find
   * the record, the form turns into the two ways of moving them.
   */
  const elsewhere = contactElsewhere(save.error);

  const detach = useMutation({
    mutationFn: () => crmApi.detachContact(contact!.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.crmCompany(companyId),
      });
      onDone();
    },
  });

  // Escape closes, matching every other overlay in the app.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-16">
      <button
        type="button"
        aria-label="Cancel"
        onClick={onCancel}
        className="absolute inset-0 -z-10"
      />

      <form
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
        /* `dvh`, not `vh`: on iOS `80vh` is measured against the viewport with
           Safari's toolbars hidden, so the last of the form -- Save included --
           sits underneath them. See `Overlay`, which this shares a shape with. */
        className="flex max-h-[calc(100dvh-5rem)] w-full max-w-md flex-col gap-2 overflow-y-auto rounded-lg border border-line bg-surface p-4 shadow-xl"
      >
        <header className="-mx-4 -mt-4 mb-2 border-b border-line px-4 py-3">
          <h2 className="text-sm font-semibold text-content">
            {contact
              ? `Edit ${contact.name ?? contact.email}`
              : `New contact at ${companyName}`}
          </h2>
        </header>

        <Field label="Email" required>
          <Input
            type="email"
            value={email}
            onChange={setEmail}
            required
            autoFocus
          />
        </Field>
        <Field label="Name">
          <Input value={name} onChange={setName} />
        </Field>
        <Field label="Role">
          <Input
            value={role}
            onChange={setRole}
            placeholder="Purchasing Manager"
          />
        </Field>
        <Field label="Phone">
          <PhoneField
            country={phone.country}
            number={phone.number}
            onCountryChange={(country) =>
              setPhone((current) => ({ ...current, country }))
            }
            onNumberChange={(number) =>
              setPhone((current) => ({ ...current, number }))
            }
          />
        </Field>
        <Field
          label="Preferred contact"
          hint="Leave unset and the CRM works it out from what they actually do"
        >
          <Picker
            value={method}
            onChange={setMethod}
            placeholder="Work it out"
            options={[
              { value: "call", label: "Call" },
              { value: "email", label: "Email" },
              { value: "whatsapp", label: "WhatsApp" },
            ]}
          />
        </Field>

        <label className="flex items-center gap-2 text-xs text-muted">
          <input
            type="checkbox"
            checked={isPrimary}
            onChange={(event) => setIsPrimary(event.target.checked)}
            className="accent-brand-600"
          />
          Main contact for this company
        </label>

        {elsewhere ? (
          <MoveContactPrompt
            companyId={companyId}
            conflict={elsewhere}
            onMoved={() => {
              void queryClient.invalidateQueries({
                queryKey: queryKeys.crmCompany(companyId),
              });
              onDone();
            }}
          />
        ) : save.isError ? (
          <p className="text-xs text-sla-breached">
            {save.error instanceof ApiError
              ? save.error.message
              : "Could not save that contact."}
          </p>
        ) : null}

        <div className="flex items-center gap-2">
          {/* Disabled on a bad number as well as a missing address: the server
              refuses it either way, and the message under the box has already
              said why, so offering the button is offering a round trip whose
              answer is on screen. */}
          <Button
            type="submit"
            size="sm"
            disabled={
              save.isPending ||
              !email.trim() ||
              !isValidPhone(phone.number, phone.country)
            }
          >
            {save.isPending ? "Saving…" : contact ? "Save" : "Add"}
          </Button>
          <Button variant="secondary" size="sm" onClick={onCancel}>
            Cancel
          </Button>

          {/*
          "Remove" detaches rather than deletes: the row is what tickets point
          at, and ingest would recreate it on their next email anyway. They are
          left belonging to no company, which is where everybody starts.
        */}
          {contact ? (
            <button
              type="button"
              onClick={() => detach.mutate()}
              disabled={detach.isPending}
              title="Leaves them belonging to no company. Their tickets are kept."
              className={cn(
                "ml-auto text-xs text-faint hover:text-sla-breached",
                detach.isPending && "opacity-50",
              )}
            >
              Remove from company
            </button>
          ) : null}
        </div>
      </form>
    </div>
  );
}

/** What the server says when the address already belongs to somebody else's account. */
interface ContactElsewhere {
  contactId: number;
  companyId: number;
  companyName: string | null;
  message: string;
}

/**
 * Read the structured 409, or null for every other failure.
 *
 * Keyed on the `code` rather than on the status, because a 409 from anywhere
 * else in this form is still just an error to show.
 */
function contactElsewhere(error: unknown): ContactElsewhere | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;

  const { code, contactId, companyId, companyName } = error.details;
  if (code !== "contact_elsewhere") return null;
  if (typeof contactId !== "number" || typeof companyId !== "number")
    return null;

  return {
    contactId,
    companyId,
    companyName: typeof companyName === "string" ? companyName : null,
    message: error.message,
  };
}

/**
 * Move them here, and decide what happens to what they did there.
 *
 * Two buttons rather than a checkbox, because neither is the default. The two
 * cases look identical from the form and want opposite answers: a person filed
 * at the wrong company never did that work there, and a person who has changed
 * employer did. A checkbox with a pre-ticked state would quietly pick one.
 */
function MoveContactPrompt({
  companyId,
  conflict,
  onMoved,
}: {
  companyId: number;
  conflict: ContactElsewhere;
  onMoved: () => void;
}) {
  const where = conflict.companyName ?? "another company";

  const move = useMutation({
    mutationFn: (withHistory: boolean) =>
      crmApi.moveContact(conflict.contactId, companyId, withHistory),
    onSuccess: onMoved,
  });

  return (
    <div className="flex flex-col gap-2 rounded-md border border-sla-warning/40 bg-sla-warning/10 px-3 py-2">
      <p className="text-xs text-content">{conflict.message}</p>

      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          disabled={move.isPending}
          title={`Their calls, notes and meetings move here too. Use this when they were filed at ${where} by mistake.`}
          onClick={() => move.mutate(true)}
        >
          Move with their history
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={move.isPending}
          title={`What they did at ${where} stays there. Use this when they have changed employer.`}
          onClick={() => move.mutate(false)}
        >
          Move, leave history at {where}
        </Button>
      </div>

      <p className="text-[11px] text-faint">
        Their tickets follow them either way — a ticket belongs to the person,
        not the account.
      </p>

      {move.isError ? (
        <p className="text-xs text-sla-breached">
          {move.error instanceof ApiError
            ? move.error.message
            : "Could not move them."}
        </p>
      ) : null}
    </div>
  );
}
